import SEO from "../components/SEO";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for Magnus Kor Talent Partners. How we collect, use, and protect your personal data. UK GDPR compliant."
        canonical="https://magnuskor.com/privacy"
      />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mb-2" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-sm text-gray-400 mb-10">Last updated: February 2026</p>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-600 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">1. Who We Are</h2>
            <p>Magnus Kor Talent Partners is a trading name of L&D Nexus Limited, a company registered in England and Wales (Company No. 16450617). Our registered address is 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom. We are the data controller for the personal data we process.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">2. What Data We Collect</h2>
            <p>We may collect the following personal data:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Contact information:</strong> name, email address, phone number</li>
              <li><strong>Professional information:</strong> current role, company, LinkedIn profile, CV/resume</li>
              <li><strong>Job application data:</strong> cover letters, work history, salary expectations</li>
              <li><strong>Client inquiry data:</strong> company details, hiring requirements, funding stage</li>
              <li><strong>Website usage data:</strong> pages visited, time on site, browser type (via cookies)</li>
              <li><strong>Testimonial data:</strong> company name, role, feedback content, company logo</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">3. How We Collect Data</h2>
            <p>We collect data when you:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Submit a contact form, job application, or candidate registration on our website</li>
              <li>Submit a client inquiry or hiring request</li>
              <li>Submit a testimonial through our testimonial form</li>
              <li>Use our free tools (Salary Calculator, Equity Calculator, Smart Hire Planner)</li>
              <li>Email us directly at recruitment@magnuskor.com or office@magnuskor.com</li>
              <li>Browse our website (via cookies and analytics)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">4. Why We Process Your Data (Legal Basis)</h2>
            <p>We process personal data under the following legal bases as defined by UK GDPR:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Consent:</strong> When you submit forms on our website, you consent to us processing your data for the stated purpose.</li>
              <li><strong>Legitimate interest:</strong> To respond to inquiries, improve our services, and communicate with prospective clients and candidates.</li>
              <li><strong>Contractual necessity:</strong> When processing is needed to fulfil a service agreement with a client.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">5. How We Use Your Data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To respond to your inquiries and provide talent advisory services</li>
              <li>To match candidates with relevant job opportunities</li>
              <li>To process job applications and client hiring requests</li>
              <li>To display approved testimonials on our website</li>
              <li>To send relevant communications (only with your consent)</li>
              <li>To improve our website and tools through aggregated analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">6. Data Sharing</h2>
            <p>We do not sell your personal data. We may share data with:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Clients:</strong> Candidate information is shared with hiring companies only with your consent and for the purpose of a specific job application.</li>
              <li><strong>Service providers:</strong> Hosting providers, analytics tools, and email services that process data on our behalf under appropriate data processing agreements.</li>
              <li><strong>Legal obligations:</strong> Where required by law, regulation, or legal process.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">7. Data Retention</h2>
            <p>We retain personal data for as long as necessary to fulfil the purposes for which it was collected:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Candidate data:</strong> Up to 24 months after last interaction, unless you request earlier deletion.</li>
              <li><strong>Client data:</strong> For the duration of the business relationship plus 6 years for legal/financial compliance.</li>
              <li><strong>Website analytics:</strong> Aggregated data retained indefinitely; individual session data for up to 14 months.</li>
              <li><strong>Testimonials:</strong> Retained until withdrawn or deleted by the submitter or an administrator.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">8. Your Rights (UK GDPR)</h2>
            <p>Under UK GDPR, you have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data.</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten").</li>
              <li><strong>Restriction:</strong> Request we limit how we process your data.</li>
              <li><strong>Portability:</strong> Request your data in a structured, machine-readable format.</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interest.</li>
              <li><strong>Withdraw consent:</strong> Where processing is based on consent, you may withdraw at any time.</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, contact us at <a href="mailto:office@magnuskor.com" className="text-[#B8860B] hover:underline">office@magnuskor.com</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">9. Data Security</h2>
            <p>We implement appropriate technical and organisational measures to protect your personal data, including encryption in transit (HTTPS), access controls, and secure cloud infrastructure. However, no method of transmission or storage is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">10. International Transfers</h2>
            <p>Your data may be processed outside the UK where our service providers are located (e.g., cloud hosting in the EU or US). Where this occurs, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the ICO.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">11. Contact & Complaints</h2>
            <p>For privacy-related inquiries, contact:</p>
            <p className="mt-2"><strong>L&D Nexus Limited</strong><br />71-75 Shelton Street, Covent Garden, London, WC2H 9JQ<br />Email: <a href="mailto:office@magnuskor.com" className="text-[#B8860B] hover:underline">office@magnuskor.com</a></p>
            <p className="mt-3">If you are not satisfied with our response, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#B8860B] hover:underline">ico.org.uk</a>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
